import React from 'react';
import { Link } from 'react-router-dom';
import CaseStudyGate from '../../components/CaseStudyGate';
import Footer from '../../components/Footer';
import OtherProjects from '../../components/OtherProjects';
import useNoIndex from '../../hooks/useNoIndex';

// Disco brand colors, sampled directly from the source deck's real product UI
// (not the decorative slide gradients) — see the "Submit"/"YES" buttons.
const ACCENT = '#2F82AC'; // real "Submit" button blue — primary accent
const TEAL = '#21B9C7'; // real "YES" button — secondary brand teal
const CORAL = '#FF615E'; // real "NO" button

// Muted sketch palette used by the hand-drawn "Landed on this UX approach"
// mockups, to visually separate them from the polished, on-brand product
// screenshots elsewhere on the page.
const SKETCH_BG = '#F5F3EC';
const INK = '#3A3A3A';

const SectionHeading = ({ eyebrow, title, intro }) => (
  <div className="mb-12">
    <p className="text-sm font-bold uppercase tracking-widest mb-3" style={{ color: ACCENT }}>
      {eyebrow}
    </p>
    <h2 className="text-3xl sm:text-4xl font-bold text-text-primary mb-4">{title}</h2>
    {intro && <p className="text-tertiary text-lg max-w-2xl">{intro}</p>}
  </div>
);

// A screenshot presented with light browser chrome, a real shadow, and a caption.
// chrome=false for shots that are already framed by something else (a phone
// bezel, a floating notification card) where a fake browser bar would be wrong.
const Shot = ({ src, alt, caption, step, chrome = true }) => (
  <figure className="flex flex-col">
    <div className="rounded-lg overflow-hidden shadow-xl border border-gray-light bg-white">
      {chrome && (
        <div className="flex items-center gap-1.5 px-3 py-2 bg-gray-light">
          <span className="w-2.5 h-2.5 rounded-full bg-gray-300" />
          <span className="w-2.5 h-2.5 rounded-full bg-gray-300" />
          <span className="w-2.5 h-2.5 rounded-full bg-gray-300" />
        </div>
      )}
      <img src={src} alt={alt} className="w-full h-auto block" />
    </div>
    <figcaption className="mt-3 text-sm text-tertiary">
      {step && (
        <span className="font-bold mr-1.5" style={{ color: ACCENT }}>
          {step}
        </span>
      )}
      {caption}
    </figcaption>
  </figure>
);

// One row inside the "Employee nomination experience" showcase — real deck
// content (see App.js's Disco section for the same teal gradient), brought
// in for "How it works" so the shipped flow gets the same fun, colorful
// treatment it had in the original pitch instead of flat, boxed screenshots.
// The images are transparent PNGs, so no card/shadow sits behind them — they
// float directly on the gradient the way they did in the deck.
const ExperiencePanel = ({ src, alt, header, callouts }) => (
  <div className="text-center">
    <p className="text-white/80 text-xs font-bold uppercase tracking-widest mb-4">{header}</p>
    <img src={src} alt={alt} className="w-3/4 h-auto mx-auto mb-6" />
    <div className="space-y-2 max-w-xs mx-auto">
      {callouts.map((text) => (
        <p key={text} className="text-white text-sm font-medium">
          {text}
        </p>
      ))}
    </div>
  </div>
);

// A step in the "current problem" / "hypothesis" flow diagrams — real content
// from the deck (slide 3 draws both as literal flow charts), rebuilt in code
// for crisper type instead of embedding the slide graphic.
const FlowStep = ({ label, tone }) => (
  <div
    className="text-sm font-medium rounded-lg px-4 py-4 flex items-center text-center min-w-[160px] sm:min-w-0 sm:flex-1"
    style={
      tone === 'solid'
        ? { backgroundColor: ACCENT, color: 'white' }
        : { border: `1.5px solid ${TEAL}`, color: '#0F5A63' }
    }
  >
    {label}
  </div>
);

const FlowDiagram = ({ steps, tone }) => (
  <div className="flex flex-col sm:flex-row items-stretch gap-3 sm:gap-0 overflow-x-auto pb-2">
    {steps.map((step, i, arr) => (
      <React.Fragment key={step}>
        <FlowStep label={step} tone={tone} />
        {i < arr.length - 1 && (
          <div className="flex sm:hidden items-center justify-center text-tertiary">↓</div>
        )}
        {i < arr.length - 1 && (
          <div className="hidden sm:flex items-center px-2 text-tertiary">···&gt;</div>
        )}
      </React.Fragment>
    ))}
  </div>
);

// One round of "Testing (rapid prototype)" — a recreated mockup of what was
// tested, plus the real learnings that came out of it. This is the page's
// strongest material, so it gets the most deliberate layout treatment.
const TestingRound = ({ round, question, mockup, learnings }) => (
  <div className="mb-16 last:mb-0">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-tertiary mb-3">{round}</p>
        <p className="text-tertiary text-lg mb-6">{question}</p>
        <div className="rounded-xl border border-gray-light bg-white p-6 shadow-lg">{mockup}</div>
      </div>
      <div>
        <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: ACCENT }}>
          What we learned
        </p>
        <ul className="space-y-3 text-tertiary text-lg">
          {learnings.map((item) => (
            <li key={item} className="flex gap-3">
              <span style={{ color: ACCENT }}>•</span>
              {item}
            </li>
          ))}
        </ul>
      </div>
    </div>
  </div>
);

// Recreated mockup: Round 1's binary yes/no nomination prompt.
const YesNoMockup = () => (
  <div>
    <p className="text-xs text-tertiary text-center mb-1">Monthly value nominations · January edition</p>
    <div className="rounded-lg border border-gray-light p-6 text-center">
      <p className="text-sm text-tertiary mb-3">Did we at Acme Inc. live this value this month?</p>
      <p className="font-bold text-text-primary text-lg mb-1">Pioneering</p>
      <p className="text-sm text-tertiary mb-5">
        Live amongst the stars. Have no fear in trying new things and taking risks.
      </p>
      <div className="flex items-center justify-center gap-4">
        <span
          className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold"
          style={{ backgroundColor: CORAL }}
        >
          NO
        </span>
        <span
          className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold"
          style={{ backgroundColor: TEAL }}
        >
          YES
        </span>
      </div>
      <p className="text-xs text-tertiary mt-4">Skip</p>
    </div>
  </div>
);

// Recreated mockup: Round 2's 1–5 scale plus peer-nomination flow.
const ScaleMockup = () => (
  <div>
    <p className="text-xs text-tertiary text-center mb-1">Monthly value nominations · January 2019</p>
    <div className="rounded-lg border border-gray-light p-6 text-center mb-4">
      <p className="text-sm text-tertiary mb-3">Did we at Acme Inc. live this value this month?</p>
      <p className="font-bold text-text-primary text-lg mb-1">Pioneering</p>
      <p className="text-sm text-tertiary mb-5">
        Understand what people want. Deliver it to them as quickly as possible.
      </p>
      <div className="flex items-center justify-center gap-2 mb-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <span
            key={n}
            className="w-9 h-9 rounded-full border-2 flex items-center justify-center text-sm font-medium"
            style={{ borderColor: TEAL, color: '#0F5A63' }}
          >
            {n}
          </span>
        ))}
      </div>
      <div className="flex justify-between text-xs text-tertiary px-1">
        <span>Not at all</span>
        <span>Very much</span>
      </div>
    </div>
    <div className="rounded-lg border border-gray-light p-4 text-left">
      <p className="text-xs text-tertiary mb-2">Nominate a co-worker who lived the "pioneering" value most in January.</p>
      <div className="rounded border border-gray-light px-3 py-2 text-sm text-tertiary">Choose a coworker</div>
    </div>
  </div>
);

// Synthesis diagram closing out "Explored and rejected": both rounds scored
// or gated the wrong thing, and the common thread pointed toward a simple,
// people-centered nomination powered by data Disco already had. Real SVG (not
// an image, and not HTML boxes plus a separately-stretched connector) so it
// scales as one uniform, precisely-aligned unit — the same construction the
// Culture Amp page's RejectionSynthesisDiagram uses: gray-light rounded rects
// for the rejected rounds, an elbow-and-chevron connector in stroke-tertiary,
// and a tinted, ACCENT-bordered rect for the direction that shipped.
const LearningsSynthesisDiagram = () => (
  <svg
    viewBox="0 0 760 300"
    className="w-full h-auto block"
    role="img"
    aria-label="Round 1, a binary yes/no survey, scored the company and not the people; Round 2, rate then nominate, added a step that didn't help anyone decide; both pointed toward peer nomination in two steps, centered on people and powered by existing Disco data"
  >
    <rect x="20" y="4" width="300" height="130" rx="14" className="fill-gray-light stroke-gray-light" strokeWidth="1" />
    <text x="170" y="34" textAnchor="middle" fontSize="19" fontWeight="700" className="fill-text-primary">
      Round 1: binary yes/no
    </text>
    <text x="170" y="58" textAnchor="middle" fontSize="19" fontWeight="700" className="fill-text-primary">
      survey
    </text>
    <text x="170" y="88" textAnchor="middle" fontSize="14" className="fill-tertiary">
      Scored the company, not the people
    </text>

    <rect x="20" y="162" width="300" height="130" rx="14" className="fill-gray-light stroke-gray-light" strokeWidth="1" />
    <text x="170" y="192" textAnchor="middle" fontSize="19" fontWeight="700" className="fill-text-primary">
      Round 2: rate, then
    </text>
    <text x="170" y="216" textAnchor="middle" fontSize="19" fontWeight="700" className="fill-text-primary">
      nominate
    </text>
    <text x="170" y="246" textAnchor="middle" fontSize="14" className="fill-tertiary">
      Added a step that didn't
    </text>
    <text x="170" y="264" textAnchor="middle" fontSize="14" className="fill-tertiary">
      help anyone decide
    </text>

    <path d="M320,69 H390 V136" fill="none" strokeWidth="2" className="stroke-tertiary" />
    <path d="M378,136 L390,148 L402,136" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="stroke-tertiary" />

    <path d="M320,227 H390 V160" fill="none" strokeWidth="2" className="stroke-tertiary" />
    <path d="M378,160 L390,148 L402,160" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="stroke-tertiary" />

    <rect x="450" y="83" width="300" height="130" rx="14" fill="rgba(47, 130, 172, 0.08)" stroke={ACCENT} strokeWidth="1.5" />
    <text x="600" y="113" textAnchor="middle" fontSize="19" fontWeight="700" fill={ACCENT}>
      Peer nomination,
    </text>
    <text x="600" y="137" textAnchor="middle" fontSize="19" fontWeight="700" fill={ACCENT}>
      two steps
    </text>
    <text x="600" y="167" textAnchor="middle" fontSize="14" fill={ACCENT}>
      Centered on people, powered by
    </text>
    <text x="600" y="185" textAnchor="middle" fontSize="14" fill={ACCENT}>
      existing Disco data
    </text>
  </svg>
);

const StatTile = ({ value, label }) => (
  <div>
    <p className="text-5xl sm:text-6xl font-bold mb-2" style={{ color: ACCENT }}>
      {value}
    </p>
    <p className="text-tertiary text-sm">{label}</p>
  </div>
);

// Hand-drawn sketch treatment for the two "Landed on this UX approach"
// illustrations (the in-Slack nomination flow and the admin export view).
// Loads the Kalam handwriting font and a shared wobble filter, scoped under
// a disco-sketch- prefix so it can't leak into the rest of the site's CSS.
// Injected imperatively (not via react-helmet) since react-helmet 6's
// side-effect model doesn't reliably commit head mutations under React 19.
const useSketchStyles = () => {
  React.useEffect(() => {
    const head = document.head;
    const nodes = [
      Object.assign(document.createElement('link'), {
        rel: 'preconnect',
        href: 'https://fonts.googleapis.com',
      }),
      Object.assign(document.createElement('link'), {
        rel: 'preconnect',
        href: 'https://fonts.gstatic.com',
        crossOrigin: 'anonymous',
      }),
      Object.assign(document.createElement('link'), {
        rel: 'stylesheet',
        href: 'https://fonts.googleapis.com/css2?family=Kalam:wght@400;700&display=swap',
      }),
      Object.assign(document.createElement('style'), {
        textContent: `
        .disco-sketch-wrap { width: 100%; max-width: 400px; margin: 0 auto; }
        .disco-sketch-card {
          position: relative;
          background: ${SKETCH_BG};
          border: 1.5px solid ${INK};
          border-radius: 255px 8px 225px 8px / 8px 225px 8px 255px;
          padding: 26px;
          height: 400px;
          overflow: hidden;
          font-family: 'Kalam', cursive;
          color: #2b3a4a;
        }
        .disco-sketch-step { position: absolute; top: 26px; left: 26px; right: 26px; bottom: 26px; }
        .disco-sketch-eyebrow { text-align: center; font-size: 12px; letter-spacing: 0.04em; color: #7d7d7d; margin-bottom: 12px; }
        .disco-sketch-title { text-align: center; font-size: 19px; font-weight: 700; margin-bottom: 12px; }
        .disco-sketch-value-icon { width: 32px; height: 32px; border-radius: 48% 52% 45% 55% / 55% 45% 55% 45%; border: 1.5px solid #4a4a4a; margin: 0 auto 8px; }
        .disco-sketch-value-name { text-align: center; font-size: 17px; font-weight: 700; margin-bottom: 6px; }
        .disco-sketch-value-desc { text-align: center; font-size: 13px; color: #5b6b7a; line-height: 1.5; margin-bottom: 14px; }
        .disco-sketch-search-field { border: 1.5px solid #4a4a4a; border-radius: 150px 10px 150px 10px / 10px 150px 10px 150px; height: 36px; display: flex; align-items: center; padding: 0 14px; font-size: 13px; color: #7d7d7d; margin-bottom: 16px; transform: rotate(-0.4deg); }
        .disco-sketch-shortlist-label { text-align: center; font-size: 13px; color: #7d7d7d; margin-bottom: 10px; }
        .disco-sketch-avatar-row { display: flex; justify-content: space-between; gap: 8px; }
        .disco-sketch-avatar-item { text-align: center; flex: 1; }
        .disco-sketch-avatar-circle { width: 34px; height: 34px; border-radius: 47% 53% 44% 56% / 56% 44% 56% 44%; border: 1.5px solid #4a4a4a; margin: 0 auto 6px; }
        .disco-sketch-avatar-name { font-size: 12px; font-weight: 700; color: #2b3a4a; margin-bottom: 2px; }
        .disco-sketch-avatar-kudos { font-size: 11px; color: #7d7d7d; }
        .disco-sketch-step2-avatar { width: 58px; height: 58px; border-radius: 46% 54% 43% 57% / 57% 43% 57% 43%; border: 1.5px solid #4a4a4a; margin: 0 auto 12px; }
        .disco-sketch-step2-title { text-align: center; font-size: 19px; font-weight: 700; margin-bottom: 6px; }
        .disco-sketch-step2-desc { text-align: center; font-size: 13px; color: #5b6b7a; line-height: 1.5; margin-bottom: 12px; }
        .disco-sketch-divider-line { border: none; border-top: 1.5px solid #8a8578; margin: 0 0 14px; transform: rotate(0.3deg); }
        .disco-sketch-why-label { font-size: 13px; color: #7d7d7d; margin-bottom: 8px; }
        .disco-sketch-why-field { border: 1.5px dashed #4a4a4a; border-radius: 8px 130px 8px 130px / 130px 8px 130px 8px; height: 72px; margin-bottom: 16px; transform: rotate(0.3deg); }
        .disco-sketch-submit-btn { border: 1.5px solid ${ACCENT}; color: ${ACCENT}; border-radius: 150px 10px 150px 10px / 10px 150px 10px 150px; height: 36px; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 700; width: 96px; margin-left: auto; transform: rotate(-0.6deg); }
        .disco-sketch-step-1 { animation: disco-sketch-step1 8s ease-in-out infinite; }
        .disco-sketch-step-2 { animation: disco-sketch-step2 8s ease-in-out infinite; }
        @keyframes disco-sketch-step1 {
          0%   { opacity: 1; transform: translateX(0); }
          38%  { opacity: 1; transform: translateX(0); }
          48%  { opacity: 0; transform: translateX(-16px); pointer-events: none; }
          88%  { opacity: 0; transform: translateX(-16px); pointer-events: none; }
          100% { opacity: 1; transform: translateX(0); }
        }
        @keyframes disco-sketch-step2 {
          0%   { opacity: 0; transform: translateX(16px); pointer-events: none; }
          38%  { opacity: 0; transform: translateX(16px); pointer-events: none; }
          48%  { opacity: 1; transform: translateX(0); }
          88%  { opacity: 1; transform: translateX(0); }
          100% { opacity: 0; transform: translateX(16px); pointer-events: none; }
        }
        .disco-sketch-caption { font-size: 14px; line-height: 1.6; color: #5b6b7a; margin-top: 16px; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif; }
        `,
      }),
    ];
    nodes.forEach((node) => head.appendChild(node));
    return () => nodes.forEach((node) => node.remove());
  }, []);
};

// The two-step nomination flow, recreated as an animated hand-drawn sketch:
// picking a shortlisted person carries their name into a "why" step.
const InSlackSketch = () => (
  <div className="disco-sketch-wrap">
    <div className="disco-sketch-card" style={{ filter: 'url(#disco-sketchy)' }}>
      <div className="disco-sketch-step disco-sketch-step-1">
        <p className="disco-sketch-eyebrow">JANUARY AWARDS</p>
        <p className="disco-sketch-title">Nominate someone for&hellip;</p>
        <div className="disco-sketch-value-icon" />
        <p className="disco-sketch-value-name">Pioneer</p>
        <p className="disco-sketch-value-desc">
          Live amongst the stars. Have no fear in trying new things and taking risks.
        </p>
        <div className="disco-sketch-search-field">Search for a co-worker</div>
        <p className="disco-sketch-shortlist-label">Or pick a top kudos receiver:</p>
        <div className="disco-sketch-avatar-row">
          <div className="disco-sketch-avatar-item">
            <div className="disco-sketch-avatar-circle" />
            <p className="disco-sketch-avatar-name">Joseph E.</p>
            <p className="disco-sketch-avatar-kudos">12 kudos</p>
          </div>
          <div className="disco-sketch-avatar-item">
            <div className="disco-sketch-avatar-circle" />
            <p className="disco-sketch-avatar-name">Callie E.</p>
            <p className="disco-sketch-avatar-kudos">8 kudos</p>
          </div>
          <div className="disco-sketch-avatar-item">
            <div className="disco-sketch-avatar-circle" />
            <p className="disco-sketch-avatar-name">Sylvia M.</p>
            <p className="disco-sketch-avatar-kudos">8 kudos</p>
          </div>
        </div>
      </div>
      <div className="disco-sketch-step disco-sketch-step-2">
        <div className="disco-sketch-step2-avatar" />
        <p className="disco-sketch-step2-title">Joseph for Pioneer</p>
        <p className="disco-sketch-step2-desc">
          Live amongst the stars. Have no fear in trying new things and taking risks.
        </p>
        <hr className="disco-sketch-divider-line" />
        <p className="disco-sketch-why-label">Tell us why Joseph exemplifies this value</p>
        <div className="disco-sketch-why-field" />
        <div className="disco-sketch-submit-btn">Submit</div>
      </div>
    </div>
    <p className="disco-sketch-caption">
      Picking a shortlisted person carries their name straight into the next step, so all that's
      left is a short note on why.
    </p>
  </div>
);

// Content for the "Messaging states" scheduling diagram — real states from
// the deck. Every card renders from one shared template; only the trigger,
// highlight, and copy change per state, which the closing callout calls out
// explicitly.
const SCHEDULE_STATES = [
  {
    trigger: 'Default',
    highlight: true,
    title: 'April Nominations are scheduled',
    sub: (
      <>
        Sending on <strong className="text-text-primary">Apr 24</strong> at 10am to{' '}
        <strong className="text-text-primary">255 teammates</strong> in #allstars, ending{' '}
        <strong className="text-text-primary">Apr 30th</strong>.
      </>
    ),
    addon: (
      <>Don't want to wait? You can send April nominations now. <span className="font-semibold underline">Send now</span></>
    ),
  },
  {
    trigger: 'If the admin enables nominations after the date already passed',
    highlight: false,
    title: 'May Nominations are scheduled',
    sub: (
      <>
        Sending on <strong className="text-text-primary">May 25</strong> at 10am to{' '}
        <strong className="text-text-primary">255 teammates</strong> in #allstars, ending{' '}
        <strong className="text-text-primary">May 31st</strong>.
      </>
    ),
    addon: (
      <>
        Looks like April 25th already passed. Want to send April nominations now?{' '}
        <span className="font-semibold underline">Send now</span>
      </>
    ),
  },
  {
    trigger: 'Once send time arrives (or admin sends early)',
    highlight: true,
    title: 'April Nominations are running',
    sub: (
      <>
        Started <strong className="text-text-primary">Apr 24</strong> at 10am to{' '}
        <strong className="text-text-primary">255 teammates</strong> in #allstars, ending{' '}
        <strong className="text-text-primary">Apr 30th</strong>.
      </>
    ),
    addon: <span className="font-semibold underline">View progress</span>,
  },
];

const SchedulingStates = () => (
  <div className="text-left">
    <div className="flex flex-col md:flex-row items-stretch gap-3 md:gap-0">
      {SCHEDULE_STATES.map((state, i, arr) => (
        <React.Fragment key={state.title}>
          <div className="flex-1 min-w-0 flex flex-col">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: ACCENT }} />
              <p className="text-[11px] font-bold uppercase tracking-wide" style={{ color: ACCENT }}>
                {state.trigger}
              </p>
            </div>
            <div
              className="rounded-lg border p-4 flex-1"
              style={
                state.highlight
                  ? { backgroundColor: 'rgba(47, 130, 172, 0.06)', borderColor: ACCENT }
                  : { backgroundColor: '#eceef2', borderColor: '#d7dbe2' }
              }
            >
              <p className="text-sm font-bold text-text-primary mb-1">{state.title}</p>
              <p className="text-xs text-tertiary leading-relaxed">{state.sub}</p>
              <div className="mt-2.5 pt-2.5 border-t border-gray-light text-xs text-tertiary leading-relaxed">
                {state.addon}
              </div>
            </div>
          </div>
          {i < arr.length - 1 && (
            <>
              <div className="flex md:hidden items-center justify-center text-tertiary py-1">↓</div>
              <div className="hidden md:flex items-center px-3 text-tertiary self-center">···&gt;</div>
            </>
          )}
        </React.Fragment>
      ))}
    </div>
    <p className="mt-4 text-xs text-tertiary flex items-center gap-1.5">
      <span className="inline-block -rotate-90">→</span> loops back to "Scheduled" for the next month
    </p>
    <p
      className="mt-6 p-4 text-sm leading-relaxed text-text-primary rounded-r-md border-l-[3px]"
      style={{ backgroundColor: 'rgba(47, 130, 172, 0.06)', borderColor: ACCENT }}
    >
      All states are the <strong>same card template</strong>: the messaging layers on
      conditionally depending on where the cycle sits relative to two deadlines (send date, end
      date). That logic also has to run against whatever timezone the admin picked, since "10am"
      isn't fixed; it's computed per admin.
    </p>
  </div>
);

const Disco = () => {
  useNoIndex();
  const IMG = '/images/case-studies/disco';
  useSketchStyles();

  return (
    <CaseStudyGate projectName="Disco">
      <div className="min-h-screen bg-primary">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="pt-8">
            <Link
              to="/"
              className="inline-flex items-center text-tertiary hover:text-[#2F82AC] transition-colors duration-300"
            >
              ← Back to Home
            </Link>
          </div>

          {/* Header */}
          <header className="pt-10 pb-16 sm:pt-16 sm:pb-20 relative">
            <img
              src={`${IMG}/discofun.png`}
              alt=""
              aria-hidden="true"
              className="hidden lg:block absolute top-0 -right-4 w-40 xl:w-48 pointer-events-none select-none"
            />
            <img src="/images/disco_logo_color.png" alt="Disco" className="h-14 sm:h-16 object-contain mb-8" />
            <h1 className="text-4xl sm:text-5xl font-bold text-text-primary leading-tight mb-6 max-w-3xl">
              Redesigning how teams celebrate each other, from a scattered survey process into a
              two-tap Slack ritual.
            </h1>
            <p className="text-tertiary text-sm mb-6"><span className="font-bold">Senior Product Designer</span> · 2019</p>
            <div className="w-16 h-1 mb-8" style={{ backgroundColor: ACCENT }} />
            <div className="grid grid-cols-[2fr_1fr] gap-6 text-sm max-w-3xl">
              <div>
                <p className="text-tertiary mb-1">Team</p>
                <p className="text-text-primary font-medium">I worked solo on this as the designer.</p>
              </div>
              <div>
                <p className="text-tertiary mb-1">Timeline</p>
                <p className="text-text-primary font-medium">3-month project · 2019</p>
              </div>
              <div>
                <p className="text-tertiary mb-1">Goal</p>
                <p className="text-text-primary font-medium">
                  Streamline values nominations to increase employee engagement
                </p>
              </div>
              <div>
                <p className="text-tertiary mb-1">Deliverable</p>
                <p className="text-text-primary font-medium">Developed product</p>
              </div>
            </div>
          </header>

          {/* Context */}
          <section className="py-16 sm:py-24 border-t border-gray-light">
            <SectionHeading eyebrow="Context" title="Recognition that never left the tools people already used" />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              <div className="lg:col-span-2 space-y-4 text-tertiary text-lg">
                <p>
Disco was a Slack-based recognition platform, the tool teams already used to send peer kudos. Companies lean on core values to align their culture and keep employees connected to the mission, but proving people actually live those values, month over month, is hard to see and even harder to reward.
                </p>
                <img src="/images/disco1.gif" alt="Disco nomination flow inside Slack" className="w-full max-w-md rounded-lg shadow-xl" />
                <p>
                  Disco set out to build a values-nomination product admins could configure once, that
                  employees could act on without ever leaving Slack: voting for teammates who
                  exemplified a value that month, right where the rest of their work already happens.
                </p>
              </div>
              <div className="bg-white rounded-lg border border-gray-light p-6 h-fit">
                <p className="text-xs font-bold uppercase tracking-widest text-tertiary mb-4">Who it's for</p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {['Admin', 'Individual Contributor'].map((persona) => (
                    <span
                      key={persona}
                      className="px-3 py-1 bg-gray-light text-sm font-medium rounded text-tertiary"
                    >
                      {persona}
                    </span>
                  ))}
                </div>
                <p className="text-tertiary text-sm">
                  Admins configure and launch monthly nominations. Individual contributors vote for
                  and nominate teammates who lived a company value that month.
                </p>
              </div>
            </div>
          </section>

          {/* Process */}
          <section className="py-16 sm:py-24 border-t border-gray-light">
            <SectionHeading eyebrow="Process" title="From a manual tool chain to one embedded flow" />

            <div className="mb-16">
              <h3 className="text-xl font-bold text-text-primary mb-2">Key research finding</h3>
              <p className="text-tertiary text-lg mb-8 max-w-2xl">
                Values nominations were already happening outside the product: some admins ran
                them by hand using Disco's own recognition data, while others leaned on tools like
                Google Forms to fill the gap. Either way, the workflow customers needed didn't
                exist yet.
              </p>
              <blockquote className="border-l-4 pl-6 py-1 mb-8 max-w-2xl" style={{ borderColor: ACCENT }}>
                <p className="text-xs font-bold uppercase tracking-widest text-tertiary mb-2">
                  Customer quotes
                </p>
                <p className="text-tertiary text-lg italic mb-4">
                  "Every month it takes me at least 5 hours to copy the kudos into a spreadsheet
                  and tally it for our values awards."
                </p>
                <p className="text-tertiary text-lg italic">
                  "We use Google surveys for our values nominations, and I have to keep track of
                  who's submitted and send reminders over and over."
                </p>
              </blockquote>
              <FlowDiagram
                tone="solid"
                steps={[
                  'Pulling recognition into a spreadsheet',
                  'Tallying kudos by value, manually',
                  'Announcing winners outside the tool',
                ]}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
              <div>
                <h3 className="text-xl font-bold text-text-primary mb-3">Current problem</h3>
                <p className="text-tertiary text-lg">
                  Customers were stitching together spreadsheets, forms, and reminders every month
                  just to run values nominations, a manual process that quietly became an
                  engagement problem.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold text-text-primary mb-3">Hypothesis</h3>
                <p className="text-tertiary text-lg mb-4">
                  This was a chance to build values nominations directly into Disco. It's core to
                  our mission (living your values daily), and the data to power it already
                  existed in the product.
                </p>
                <blockquote className="border-l-4 pl-6 py-1" style={{ borderColor: ACCENT }}>
                  <p className="text-xs font-bold uppercase tracking-widest text-tertiary mb-2">
                    Customer quote
                  </p>
                  <p className="text-tertiary text-lg italic">
                    "It would be great if Disco could show us if we as a company are living our values"
                  </p>
                </blockquote>
              </div>
            </div>

            <div className="mb-16">
              <h3 className="text-xl font-bold text-text-primary mb-5">Explored and rejected</h3>
              <TestingRound
                round="Round 1"
                question="Do our employees feel we live our company values? How can Disco help us gather this feedback?"
                mockup={<YesNoMockup />}
                learnings={[
                  'Replace binary yes/no responses with more nuanced options.',
                  'Allow employees to nominate colleagues who best exemplify company values.',
                ]}
              />

              <TestingRound
                round="Round 2"
                question="How can we easily identify and reward those who exemplify our values through peer feedback?"
                mockup={<ScaleMockup />}
                learnings={[
                  'Focus on the peer-to-peer nomination process: this was a big admin pain point.',
                  "Enable nominators to explain why they're recognizing this person.",
                ]}
              />

              <div className="mt-10 pt-8 border-t border-gray-light">
                <p className="text-xs font-bold uppercase tracking-widest text-tertiary mb-2">Learnings</p>
                <p className="text-tertiary text-lg max-w-3xl mb-8">
                  Both rounds failed for related reasons: the yes/no survey scored the company
                  instead of the people in it, and the rating step asked for a judgment that didn't
                  help anyone decide who to recognize. The common thread pointed toward focusing
                  entirely on people: cutting the process down to a simple two-step nomination, and
                  pulling in data Disco already had to shortlist top kudos recipients before anyone
                  had to search for a name.
                </p>
                <div className="mt-4 max-w-2xl mx-auto">
                  <LearningsSynthesisDiagram />
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold text-text-primary mb-4">Landed on this UX approach</h3>
              <p className="text-tertiary text-lg mb-8 max-w-2xl">
                Let employees quickly vote on who exemplified each company value the most each month
                or quarter, and give them room to say why.
              </p>
              <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
                <filter id="disco-sketchy">
                  <feTurbulence type="fractalNoise" baseFrequency="0.012 0.025" numOctaves="2" seed="7" result="noise" />
                  <feDisplacementMap in="SourceGraphic" in2="noise" scale="2.6" xChannelSelector="R" yChannelSelector="G" />
                </filter>
              </svg>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-tertiary mb-3">
                  In Slack experience
                </p>
                <InSlackSketch />
              </div>
            </div>
          </section>

          {/* Final design */}
          <section className="py-16 sm:py-24 border-t border-gray-light">
            <SectionHeading
              eyebrow="Final design"
              title="The finished flow, from nomination to admin export"
              intro="High-fidelity screens of what shipped, for the employees using it."
            />

            <div className="mb-20">
              <div
                className="relative"
                style={{
                  background: 'linear-gradient(to bottom, #23AFC3 0%, #2A91B2 70%, #2B8EB0 100%)',
                  marginLeft: 'calc(50% - 50vw)',
                  marginRight: 'calc(50% - 50vw)',
                }}
              >
                {/*
                  A standalone decorative layer, not tied into either panel's
                  layout — sits on its own plane in the top-right corner of
                  the card. Hidden below lg (mobile/tablet) since the content
                  column is a fixed max-w-4xl: below that breakpoint there
                  isn't reliably enough gradient margin beside it to avoid
                  overlapping the images.
                */}
                <img
                  src={`${IMG}/discofun.png`}
                  alt=""
                  aria-hidden="true"
                  className="hidden lg:block absolute top-8 right-8 w-40 xl:w-64 2xl:w-80 pointer-events-none select-none"
                />
                <div className="max-w-4xl mx-auto px-6 sm:px-12 py-12 sm:py-16 relative">
                  <p className="text-white font-bold text-2xl sm:text-3xl text-center mb-10">
                    Employee nomination experience
                  </p>
                  <div className="flex flex-col gap-16">
                    <ExperiencePanel
                      src={`${IMG}/disco-alert.png`}
                      alt="Slack messages nudging a nomination, then sharing the results"
                      header="Slack notifications"
                      callouts={[
                        'Time-based notifications and reminders in Slack and Microsoft Teams.',
                        'Notifications about the results after nominations ended.',
                      ]}
                    />
                    <ExperiencePanel
                      src={`${IMG}/disco-app.png`}
                      alt="Nominating a coworker for a value, then explaining why"
                      header="Nomination flow"
                      callouts={[
                        'Surfacing Disco social data on recognition received for each value.',
                        'Simple input flow.',
                      ]}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold text-text-primary mb-2">What shipped for admins</h3>
              <p className="text-tertiary mb-6 max-w-2xl text-lg">
                Working directly with customers, the nomination flow shipped alongside admin controls
                to set up, configure, and schedule it.
              </p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch mb-8">
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-tertiary mb-3">Onboarding</p>
                  <Shot
                    src={`${IMG}/admin-values.png`}
                    alt="Initial settings screen prompting admins to add company values before enabling nominations"
                    step="1"
                    caption="This is the initial settings screen: if an admin tries to enable values nominations before adding company values, they're prompted to add them here first."
                  />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-tertiary mb-3">
                    Enabling nominations
                  </p>
                  <Shot
                    src={`${IMG}/admin-settings-panel.png`}
                    alt="Admin settings panel for enabling value nominations"
                    step="2"
                    caption="Then turn nominations on, name them, and choose who's included. No engineering ticket required."
                  />
                </div>
              </div>
              <div className="mb-8">
                <p className="text-xs font-bold uppercase tracking-widest text-tertiary mb-3">Messaging states</p>
                <SchedulingStates />
                <p className="mt-3 text-sm text-tertiary">
                  <span className="font-bold mr-1.5" style={{ color: ACCENT }}>
                    3
                  </span>
                  The full nomination cycle, from scheduled to running, runs on one card template,
                  so the messaging state is always clear.
                </p>
              </div>
              <div className="max-w-2xl">
                <p className="text-xs font-bold uppercase tracking-widest text-tertiary mb-3">Data export</p>
                <Shot
                  src={`${IMG}/admin-results-panel.png`}
                  alt="Admin dashboard showing monthly nomination results, open and closed, with export links"
                  caption="Every month's results and top nominees, with export built in, even before the current cycle closes."
                />
              </div>
            </div>
          </section>

          {/* Outcome */}
          <section className="py-16 sm:py-24 border-t border-gray-light">
            <SectionHeading eyebrow="Outcome" title="Measured after launch" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-10">
              <StatTile
                value="3.5x"
                label="increase in employee engagement with values nominations, compared to previous methods"
              />
              <StatTile
                value="95%"
                label="reduction in admin time to run and calculate values nominations, including setup, messaging, reminders, and calculations"
              />
              <StatTile
                value="300K"
                label="employees at our largest enterprise customer to date, closed post-launch and significantly boosting company revenue"
              />
            </div>
          </section>

          <OtherProjects current="disco" />
        </div>
      </div>
      <Footer />
    </CaseStudyGate>
  );
};

export default Disco;
